import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { Facility } from '@reliance/types/models/Facility';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import {
  IEventNotificationAction,
  IEventNotificationTrigger,
} from './event-notification-factory';
import eventNotificationService, { IPREAConfig } from './event-notification-service';
import PreaRecipientEditModal from './PreaRecipientEditModal';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useAlerts } from '../alert/AlertContext';

type PreaRecipientRow = {
  triggerId: number;
  actionId: number;
  name: string;
  email: string;
};

function actionTemplateIdFromAction(action: IEventNotificationAction): number | undefined {
  const ext = action as IEventNotificationAction & { actionTemplate?: { id?: number } };
  return ext.actionTemplate?.id ?? action.actionTemplateId;
}

type PreaConfigurationsProps = {
  facility: Facility;
};

const PreaConfigurations: React.FC<PreaConfigurationsProps> = ({ facility }) => {
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [rows, setRows] = useState<PreaRecipientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingPrea, setDeletingPrea] = useState(false);
  const [editRow, setEditRow] = useState<PreaRecipientRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<PreaRecipientRow | null>(null);
  const alerts = useAlerts();

  useNotifyTabLoading('alerts', loading || adding || deletingPrea);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const trigger = await eventNotificationService.getPREATrigger(facility.id);
      if (trigger?.id != null) {
        const actions = eventNotificationService.getEmailActions(trigger);
        const next: PreaRecipientRow[] = [];
        for (const action of actions) {
          if (action.id == null) {
            continue;
          }
          next.push({
            triggerId: trigger.id,
            actionId: action.id,
            name: action.destinationName ?? '',
            email: action.destination ?? '',
          });
        }
        setRows(next);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [eventNotificationService, facility.id]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addName.trim() || !addEmail.trim()) {
      return;
    }
    setAdding(true);
    try {
      const preaTrigger: IEventNotificationTrigger | undefined =
        await eventNotificationService.getPREATrigger(facility.id);
      const config: IPREAConfig = {
        emailAction: {
          name: addName.trim(),
          address: addEmail.trim(),
        },
      };
      if (preaTrigger?.id != null && (preaTrigger.actions?.length ?? 0) > 0) {
        config.triggerId = preaTrigger.id;
        const first = preaTrigger.actions[0];
        const tid = actionTemplateIdFromAction(first);
        if (tid != null) {
          config.templateId = tid;
        }
      }
      const res = await eventNotificationService.createPREATriggerAndOrAction(config, facility);
      if (!res.data?.success) {
        throw new Error('PREA create failed');
      }
      alerts.success('The PREA trigger action was successfully added');
      setAddName('');
      setAddEmail('');
      await loadRows();
    } catch {
      alerts.error('An error occurred while creating PREA trigger');
    } finally {
      setAdding(false);
    }
  };

  const performDelete = async (row: PreaRecipientRow) => {
    setDeletingPrea(true);
    try {
      await eventNotificationService.deleteAction(row.triggerId, row.actionId);
      alerts.success('The PREA trigger action was successfully deleted');
      setDeleteRow(null);
      await loadRows();
    } catch {
      alerts.error('An error occurred while deleting PREA trigger action');
    } finally {
      setDeletingPrea(false);
    }
  };

  const addDisabled = adding || !addName.trim() || !addEmail.trim();

  return (
    <>
      <form onSubmit={handleAdd}>
          <div className="form-horizontal card card-primary">
            <div className="card-header">Add PREA email recipient</div>
            <div className="card-body">
              <div className="col-md-4">
                <div className="form-group">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={addEmail}
                    onChange={(ev) => setAddEmail(ev.target.value)}
                    placeholder="Recipient email address"
                    required
                  />
                </div>
                <div className="form-group">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={addName}
                    onChange={(ev) => setAddName(ev.target.value)}
                    placeholder="Recipient name"
                    required
                  />
                </div>
              </div>
              <div className="col-md-8" />
            </div>
            <div className="card-footer">
              <Button type="submit" variant="primary" disabled={addDisabled}>
                {adding ? 'Adding...' : 'Add'}
              </Button>
            </div>
          </div>
        </form>

        <Row className="mt-3">
          <Col md={12}>
            {loading && rows.length === 0 ? (
              <p className="text-muted">Loading…</p>
            ) : (
              <table className="table table-striped table-hover table-bordered table-condensed table-sm nowrap">
                <thead>
                  <tr>
                    <th>Recepient Name</th>
                    <th>Recepient Email</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.triggerId}-${row.actionId}`}>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td className="text-center text-nowrap">
                        <Button
                          variant="default"
                          size="sm"
                          className="me-1"
                          title="Edit"
                          aria-label={`Edit PREA recipient ${row.name}`}
                          onClick={() => setEditRow(row)}>
                          <i className="fas fa-edit" />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          title="Remove"
                          aria-label={`Remove PREA recipient ${row.name}`}
                          disabled={deletingPrea}
                          onClick={() => setDeleteRow(row)}>
                          <i className="fas fa-trash-alt" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Col>
        </Row>

      {editRow != null && (
        <PreaRecipientEditModal
          triggerId={editRow.triggerId}
          actionId={editRow.actionId}
          initialName={editRow.name}
          initialEmail={editRow.email}
          eventNotificationService={eventNotificationService}
          onClose={(success) => {
            setEditRow(null);
            if (success) {
              void loadRows();
            }
          }}
        />
      )}

      {deleteRow != null && (
        <ConfirmModal
          show
          title="Remove PREA recipient"
          acceptText="Remove"
          rejectText="Cancel"
          onClose={() => setDeleteRow(null)}
          onAccept={async () => {
            await performDelete(deleteRow);
          }}>
          Are you sure you want to remove this PREA recipient?
        </ConfirmModal>
      )}
    </>
  );
};

export default PreaConfigurations;
